import { EllipsisVertical } from "lucide-react";
import React, { cloneElement, useState } from 'react';
import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useMergeRefs,
    useRole,
} from '@floating-ui/react';

import Button from '../button/Button';
import Tooltip from '../tooltip/Tooltip';
import './dropdown.css';

const DEFAULT_PLACEMENT = 'bottom-end';

/**
 * Action menu dropdown (portaled so table overflow does not clip the menu).
 */
const Dropdown = ({
    id,
    items = [],
    disabled = false,
    className = '',
    dataCyTrigger,
    trigger,
    triggerText,
    triggerVariant = 'tertiary',
    triggerSize = 'sm',
    placement = DEFAULT_PLACEMENT,
    dismissOnClick = true,
}) => {
    const [open, setOpen] = useState(false);

    const allItemsDisabled = items.length > 0 && items.every((item) => item.disabled);
    const isDisabled = disabled || items.length === 0 || allItemsDisabled;

    const { refs, floatingStyles, context } = useFloating({
        open: open && !isDisabled,
        onOpenChange: (next) => {
            if (!isDisabled) {
                setOpen(next);
            }
        },
        placement,
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(8),
            flip({ padding: 8 }),
            shift({ padding: 8 }),
        ],
    });

    const click = useClick(context, { enabled: !isDisabled });
    const dismiss = useDismiss(context);
    const menuRole = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        click,
        dismiss,
        menuRole,
    ]);

    const triggerDataCy = dataCyTrigger || `dropdown-trigger-${id || 'default'}`;
    const mergedTriggerRef = useMergeRefs([refs.setReference, trigger?.ref]);

    const referenceProps = getReferenceProps({
        'aria-expanded': open,
        'aria-haspopup': 'menu',
    });

    let referenceElement;
    if (trigger) {
        referenceElement = cloneElement(trigger, {
            ...referenceProps,
            ref: mergedTriggerRef,
            disabled: isDisabled || trigger.props.disabled,
        });
    } else if (triggerText) {
        referenceElement = (
            <Button
                variant={triggerVariant}
                size={triggerSize}
                disabled={isDisabled}
                data-cy={triggerDataCy}
                ref={refs.setReference}
                {...referenceProps}
            >
                {triggerText}
            </Button>
        );
    } else {
        referenceElement = (
            <button
                type="button"
                className={`rounded-md p-[10px] transition-colors hover:bg-gray-100 ${
                    isDisabled
                        ? 'cursor-not-allowed text-gray-400'
                        : 'text-gray-600 hover:text-gray-800'
                }`}
                disabled={isDisabled}
                data-cy={triggerDataCy}
                ref={refs.setReference}
                {...referenceProps}
            >
                <EllipsisVertical className="h-5 w-5" />
            </button>
        );
    }

    return (
        <div className={`inline-block ${className}`.trim()}>
            {referenceElement}
            {open && !isDisabled && (
                <FloatingPortal>
                    <FloatingFocusManager context={context} modal={false}>
                        <ul
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className="june-dropdown-menu"
                            data-testid="flowbite-dropdown"
                            {...getFloatingProps()}
                        >
                            {items.map((item, index) => {
                                const menuItem = (
                                    <button
                                        type="button"
                                        className={`june-dropdown-item dropdown-item-${item.variant || 'normal'}`}
                                        disabled={item.disabled}
                                        data-cy={item.dataCy}
                                        {...getItemProps({
                                            onClick: (event) => {
                                                event.preventDefault();
                                                if (item.disabled) {
                                                    return;
                                                }
                                                item.onClick?.();
                                                if (dismissOnClick) {
                                                    setOpen(false);
                                                }
                                            },
                                        })}
                                    >
                                        {item.label}
                                    </button>
                                );

                                if (!item.title) {
                                    return (
                                        <li key={`${item.label}-${index}`} className="list-none">
                                            {menuItem}
                                        </li>
                                    );
                                }

                                return (
                                    <li key={`${item.label}-${index}`} className="list-none">
                                        <Tooltip content={item.title} position="left">
                                            <div className="w-full">{menuItem}</div>
                                        </Tooltip>
                                    </li>
                                );
                            })}
                        </ul>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </div>
    );
};

export default Dropdown;
